/**
 * api.js
 * The only file in the UI that talks to the Worker.
 *
 * Everything goes through /api/*, which is also the surface an MCP client
 * would use later — so nothing here may ever become UI-specific.
 */

/**
 * Whoever wants to know that the session has gone. Registered by App so the
 * whole app can show one sign-in prompt rather than each screen inventing its
 * own explanation.
 */
let onSignedOut = null;
export const setSignedOutHandler = (fn) => {
  onSignedOut = fn;
};

/** The error every request throws once Cloudflare Access has stopped trusting us. */
function signedOut() {
  onSignedOut?.();
  return new ApiError(
    'session_expired',
    'Your sign-in has expired. Reload the page to sign in again.',
    401,
  );
}

async function request(path, { method = 'GET', body, signal } = {}) {
  let res;
  try {
    res = await fetch(`/api${path}`, {
      method,
      signal,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      // When the Access session expires, /api/* answers with a redirect to the
      // Cloudflare login page. Following it returns HTML with status 200 —
      // which used to parse as "no JSON, but res.ok is true", so this function
      // returned null and every screen rendered an empty state instead of
      // saying you had been signed out. `manual` turns that into a signal we
      // can actually recognise.
      redirect: 'manual',
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    // A same-origin call cannot fail at the network level for any ordinary
    // reason; in practice this is the Access bounce too.
    throw signedOut();
  }

  if (res.type === 'opaqueredirect' || res.status === 0 || res.status === 302) {
    throw signedOut();
  }

  let payload = null;
  let parsed = false;
  try {
    payload = await res.json();
    parsed = true;
  } catch {
    // Non-JSON with a successful status is the Access login page, or a proxy
    // in the way. Either way we are not talking to the API.
    if (res.ok) throw signedOut();
  }

  if (!res.ok) {
    const e = parsed ? (payload?.error ?? {}) : {};
    throw new ApiError(
      e.code ?? 'error',
      e.message ?? `Request failed (${res.status}).`,
      res.status,
      e.detail,
    );
  }
  return payload;
}

/** Reads size and duration from the file itself, before it is uploaded. */
async function measure(file) {
  if (!file.type.startsWith('video/')) return {};

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve({
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        duration: Number.isFinite(video.duration) ? Math.round(video.duration * 10) / 10 : null,
      });
    };
    // A codec the browser cannot read is still worth uploading; the platforms
    // may accept it even when Chrome will not preview it.
    video.onerror = () => resolve({});
    video.src = URL.createObjectURL(file);
  });
}

/** The wire shape of an auto-schedule pattern, in one place. */
const body = (p) => ({
  brand_id: p.brandId,
  mode: p.mode,
  weekdays: p.weekdays ?? [],
  month_days: p.monthDays ?? [],
  times: p.times ?? [],
  start_date: p.startDate ?? null,
  post_ids: p.postIds ?? null,
});

/** One multipart POST, shared by preview and import. */
async function sendFile(url, { brandId, file, defaultTime }) {
  const form = new FormData();
  form.append('file', file);
  form.append('brand_id', brandId);
  if (defaultTime) form.append('default_time', defaultTime);
  return unwrap(await fetch(url, { method: 'POST', body: form }));
}

async function unwrap(res) {
  if (res.type === 'opaqueredirect' || res.status === 0 || res.status === 302) throw signedOut();

  const payload = await res.json().catch(() => null);
  // An upload that comes back as the login page must not look like a success
  // with nothing in it.
  if (res.ok && payload === null) throw signedOut();

  if (!res.ok) {
    const e = payload?.error ?? {};
    throw new ApiError(e.code ?? 'error', e.message ?? 'Upload failed.', res.status, e.detail);
  }
  return payload;
}

export class ApiError extends Error {
  constructor(code, message, status, detail) {
    super(message);
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

export const api = {
  health: () => request('/health'),
  // `days` scopes the figures, the chart and every "vs" comparison on the
  // dashboard to the same window.
  dashboard: ({ days = 30 } = {}) => request(`/dashboard?days=${days}`),

  meta: {
    // Whether a Facebook login is connected, and how long its token has left.
    status: () => request('/auth/meta/status'),
    // Pages this login administers. Page tokens never leave the Worker.
    pages: () => request('/auth/meta/pages'),
    connect: (brandId, pageId) =>
      request('/auth/meta/connect', { method: 'POST', body: { brand_id: brandId, page_id: pageId } }),
    disconnect: (accountId) =>
      request('/auth/meta/disconnect', { method: 'POST', body: { account_id: accountId } }),
    // A full page navigation, not a fetch — Facebook needs to own the window.
    signInUrl: '/api/auth/meta/start',
  },

  media: {
    list: (brandId) => request(`/media?brand_id=${encodeURIComponent(brandId)}`),
    // Preview inside the app. Access has already authenticated the browser.
    rawUrl: (id) => `/api/media/${id}/raw`,
    remove: (id) => request(`/media/${id}`, { method: 'DELETE' }),

    /** The screen-reader description, sent to Meta when the picture is posted. */
    describe: (id, altText) =>
      request(`/media/${id}`, { method: 'PATCH', body: { alt_text: altText } }),
    /**
     * Images go as multipart; video streams as a raw body so a 90MB file is
     * never buffered in the Worker. Dimensions and duration are measured here
     * because nothing server-side can decode a video.
     */
    async upload(brandId, file, altText = '') {
      const meta = await measure(file);

      if (file.type.startsWith('video/')) {
        const q = new URLSearchParams({
          brand_id: brandId,
          type: file.type,
          bytes: String(file.size),
          alt_text: altText,
          ...(meta.width ? { width: String(meta.width), height: String(meta.height) } : {}),
          ...(meta.duration ? { duration: String(meta.duration) } : {}),
        });
        const res = await fetch(`/api/media/upload-stream?${q}`, { method: 'PUT', body: file });
        return unwrap(res);
      }

      const form = new FormData();
      form.append('file', file);
      form.append('brand_id', brandId);
      form.append('alt_text', altText);
      const res = await fetch('/api/media/upload', { method: 'POST', body: form });
      return unwrap(res);
    },
  },

  /**
   * Spreadsheet in, spreadsheet out.
   *
   * Preview and import send the same file to two endpoints on purpose: the
   * server plans the file the same way both times, so what you were shown and
   * what gets created can never disagree. Uploading twice costs a second and
   * buys that guarantee.
   */
  bulk: {
    preview: ({ brandId, file, defaultTime }) =>
      sendFile('/api/bulk/preview', { brandId, file, defaultTime }),

    // Which images a spreadsheet can actually name. Without this the Media
    // column is guesswork.
    media: (brandId) => request(`/bulk/media?brand_id=${encodeURIComponent(brandId)}`),

    import: ({ brandId, file, defaultTime, schedule = false }) =>
      sendFile(
        `/api/bulk/import?confirm=true${schedule ? '&schedule=true' : ''}`,
        { brandId, file, defaultTime },
      ),
  },

  /** Other accounts, tracked over time. */
  competitors: {
    list: (brandId) => request(`/competitors?brand_id=${encodeURIComponent(brandId)}`),
    get: (id) => request(`/competitors/${id}`),
    add: ({ brandId, username, label = '', notes = '' }) =>
      request('/competitors', {
        method: 'POST',
        body: { brand_id: brandId, username, label, notes },
      }),
    update: (id, body) => request(`/competitors/${id}`, { method: 'PATCH', body }),
    remove: (id) => request(`/competitors/${id}`, { method: 'DELETE' }),
    refresh: (brandId) =>
      request('/competitors/refresh', { method: 'POST', body: { brand_id: brandId } }),
  },

  /** Who is on the team, and what they have done. */
  team: {
    get: () => request('/team'),
    add: ({ email, name, role, brandIds = [] }) =>
      request('/team', { method: 'POST', body: { email, name, role, brand_ids: brandIds } }),
    update: (email, body) =>
      request(`/team/${encodeURIComponent(email)}`, { method: 'PATCH', body }),
    remove: (email) => request(`/team/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    activity: ({ days = 30, actor = null, brandId = null } = {}) => {
      const q = new URLSearchParams({ days: String(days) });
      if (actor) q.set('actor', actor);
      if (brandId) q.set('brand_id', brandId);
      return request(`/team/activity?${q}`);
    },
  },

  /**
   * Sending posts to someone for approval.
   *
   * `create` returns the link exactly once. Nothing else in this section can
   * retrieve it, because the server stores only its hash.
   */
  reviews: {
    list: (brandId) => request(`/reviews?brand_id=${encodeURIComponent(brandId)}`),

    create: ({ brandId, name, message, campaignId = null, postIds = [], days, canDecide }) =>
      request('/reviews', {
        method: 'POST',
        body: {
          brand_id: brandId,
          name,
          message,
          campaign_id: campaignId,
          post_ids: postIds,
          days,
          can_decide: canDecide,
        },
      }),

    revoke: (id) => request(`/reviews/${id}`, { method: 'DELETE' }),
    feedback: (id) => request(`/reviews/${id}/feedback`),
    forPosts: (postIds) =>
      request('/reviews/feedback', { method: 'POST', body: { post_ids: postIds } }),
  },

  /** The public link-in-bio page. */
  bio: {
    get: (brandId) => request(`/bio?brand_id=${encodeURIComponent(brandId)}`),
    create: ({ brandId, slug }) =>
      request('/bio', { method: 'POST', body: { brand_id: brandId, slug } }),
    update: (id, body) => request(`/bio/${id}`, { method: 'PATCH', body }),

    addLink: (pageId, body) => request(`/bio/${pageId}/links`, { method: 'POST', body }),
    updateLink: (linkId, body) => request(`/bio/links/${linkId}`, { method: 'PATCH', body }),
    removeLink: (linkId) => request(`/bio/links/${linkId}`, { method: 'DELETE' }),
    reorder: (pageId, ids) => request(`/bio/${pageId}/reorder`, { method: 'POST', body: { ids } }),
  },

  /**
   * Hashtag research.
   *
   * `lookup` is the only call in the whole API that spends a rationed external
   * allowance, so every response carries the remaining quota rather than making
   * the UI ask for it separately.
   */
  hashtags: {
    get: (brandId) => request(`/hashtags?brand_id=${encodeURIComponent(brandId)}`),

    lookup: ({ brandId, tag, refresh = false }) =>
      request('/hashtags/lookup', { method: 'POST', body: { brand_id: brandId, tag, refresh } }),

    suggest: ({ brandId, topic, avoid = [] }) =>
      request('/hashtags/suggest', { method: 'POST', body: { brand_id: brandId, topic, avoid } }),

    check: ({ tags, platform = 'instagram' }) =>
      request('/hashtags/check', { method: 'POST', body: { tags, platform } }),

    sets: (brandId) => request(`/hashtags/sets?brand_id=${encodeURIComponent(brandId)}`),
    saveSet: ({ brandId, id = null, name, tags, notes = '' }) =>
      request('/hashtags/sets', {
        method: 'POST',
        body: { brand_id: brandId, id, name, tags, notes },
      }),
    removeSet: (id) => request(`/hashtags/sets/${id}`, { method: 'DELETE' }),
  },

  /**
   * Dropping a queue of drafts into a repeating pattern.
   *
   * `apply` is the one call in the app that can commit a month of posts at
   * once, so it carries confirm=true the way publishing does.
   */
  autoschedule: {
    get: (brandId) => request(`/autoschedule?brand_id=${encodeURIComponent(brandId)}`),

    preview: (p) => request('/autoschedule/preview', { method: 'POST', body: body(p) }),

    apply: (p) =>
      request('/autoschedule/apply?confirm=true', {
        method: 'POST',
        body: { ...body(p), schedule: p.schedule !== false },
      }),
  },

  /**
   * The keyword rules that answer comments privately.
   *
   * Switching them on and running them by hand both carry confirm, because
   * both put words in front of a stranger under the brand's name.
   */
  autoreply: {
    get: () => request('/autoreply'),
    preview: () => request('/autoreply/preview'),
    tryIt: (b) => request('/autoreply/try', { method: 'POST', body: b }),

    setEnabled: (enabled) =>
      request('/autoreply/enabled', { method: 'POST', body: { enabled, confirm: true } }),

    add: (b) => request('/autoreply/rules', { method: 'POST', body: b }),
    update: (id, b) => request(`/autoreply/rules/${id}`, { method: 'PATCH', body: b }),
    remove: (id) => request(`/autoreply/rules/${id}`, { method: 'DELETE' }),

    runNow: () => request('/autoreply/run?confirm=true', { method: 'POST' }),
  },

  /** The recycling rotation. */
  evergreen: {
    get: (brandId) => request(`/evergreen?brand_id=${encodeURIComponent(brandId)}`),

    add: (body) => request('/evergreen', { method: 'POST', body }),
    update: (id, body) => request(`/evergreen/${id}`, { method: 'PATCH', body }),
    remove: (id) => request(`/evergreen/${id}`, { method: 'DELETE' }),

    addSlot: ({ brandId, category, weekday, timeLocal, platforms = null }) =>
      request('/evergreen/slots', {
        method: 'POST',
        body: { brand_id: brandId, category, weekday, time_local: timeLocal, platforms },
      }),
    setSlotEnabled: (id, enabled) =>
      request(`/evergreen/slots/${id}`, { method: 'PATCH', body: { enabled } }),
    removeSlot: (id) => request(`/evergreen/slots/${id}`, { method: 'DELETE' }),

    setAutoSchedule: (enabled) =>
      request('/evergreen/auto-schedule', { method: 'POST', body: { enabled } }),
    run: () => request('/evergreen/run', { method: 'POST', body: {} }),
  },

  /** Your own websites: visits, and which app people download. */
  website: {
    get: (days = 30) => request(`/website?days=${days}`),
    addSite: (host, brandId = null) =>
      request('/website/sites', { method: 'POST', body: { host, brand_id: brandId } }),
    setSiteEnabled: (id, enabled) =>
      request(`/website/sites/${id}`, { method: 'PATCH', body: { enabled } }),
    removeSite: (id) => request(`/website/sites/${id}`, { method: 'DELETE' }),

    // `match` is what a clicked address must contain for it to count as this
    // app — so an existing download button counts without being changed.
    // `siteId` is which app's figures it belongs in; left out, the Worker works
    // it out from the rule, the destination or the brand.
    addLink: ({ label, url, brandId = null, match = null, siteId = null }) =>
      request('/website/links', {
        method: 'POST',
        body: { label, url, brand_id: brandId, match_url: match, site_id: siteId },
      }),
    updateLink: (id, body) => request(`/website/links/${id}`, { method: 'PATCH', body }),
    /** Files a download under an app, which is what the cards are grouped by. */
    assignLink: (id, siteId) =>
      request(`/website/links/${id}`, { method: 'PATCH', body: { site_id: siteId } }),
    // Retires a link and keeps its history, so past figures never change.
    archiveLink: (id) => request(`/website/links/${id}`, { method: 'DELETE' }),
  },

  posts: {
    list: ({ status = null, brandId = null } = {}) => {
      const q = new URLSearchParams();
      if (status) q.set('status', status);
      if (brandId) q.set('brand_id', brandId);
      return request(`/posts${q.toString() ? `?${q}` : ''}`);
    },
    get: (id) => request(`/posts/${id}`),
    create: (data) => request('/posts', { method: 'POST', body: data }),
    update: (id, data) => request(`/posts/${id}`, { method: 'PATCH', body: data }),
    remove: (id) => request(`/posts/${id}`, { method: 'DELETE' }),
    // Retries only the failed half, so a partial success never double-posts.
    retry: (id) => request(`/posts/${id}/retry`, { method: 'POST', body: { confirm: true } }),
    // Deletes the failed halves of a post. They never went out, so no platform changes.
    discardFailed: (id) =>
      request(`/posts/${id}/discard-failed`, { method: 'POST', body: { confirm: true } }),
    // Deletes one published half from Facebook or Instagram. Irreversible; the API insists on confirm.
    removeFromPlatform: (targetId) =>
      request(`/posts/targets/${targetId}/remove`, { method: 'POST', body: { confirm: true } }),
    // Exactly which accounts a post will reach — shown before publishing.
    destinations: (id) => request(`/posts/${id}/destinations`),
    // `confirm` is required by the API, not just the UI: nothing publishes implicitly.
    publish: (id) => request(`/posts/${id}/publish`, { method: 'POST', body: { confirm: true } }),
    // whenLocal is London wall-clock time ("2026-09-08T19:30"); the Worker
    // converts it to UTC once, and everything below stays in UTC.
    schedule: (id, whenLocal) =>
      request(`/posts/${id}/schedule`, { method: 'POST', body: { when_local: whenLocal } }),
    cancel: (id) => request(`/posts/${id}/cancel`, { method: 'POST', body: {} }),
    // Moves a draft's intended slot without scheduling it. Campaign posts carry
    // a time from the moment they are planned, but stay drafts until the week
    // is approved.
    setSlot: (id, whenLocal) => request(`/posts/${id}`, { method: 'PATCH', body: { when_local: whenLocal } }),
    calendar: (from, to) =>
      request(`/posts/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  },

  /**
   * The Copilot: a brand brief in, a month of drafts out.
   *
   * `strategy` and `month` are two deliberately separate calls. The first is
   * cheap and changes nothing; the second spends more and creates thirty
   * drafts, which is why it carries confirm the way publishing does.
   */
  copilot: {
    get: ({ brandId, month = null }) => {
      const q = new URLSearchParams({ brand_id: brandId });
      if (month) q.set('month', month);
      return request(`/copilot?${q}`);
    },

    strategy: ({ brandId, month, goal = '' }) =>
      request('/copilot/strategy', {
        method: 'POST',
        body: { brand_id: brandId, month, goal },
      }),

    // The only Copilot call that creates anything.
    month: (strategyId) =>
      request('/copilot/month', {
        method: 'POST',
        body: { strategy_id: strategyId, confirm: true },
      }),

    // A whole-list replace: the order decides which slot each idea lands in.
    saveIdeas: (id, ideas) =>
      request(`/copilot/${id}/ideas`, { method: 'PATCH', body: { ideas } }),

    remove: (id) => request(`/copilot/${id}`, { method: 'DELETE' }),

    /** One question, one answer. The conversation lives in the screen, not the server. */
    ask: ({ brandId, month, question, history = [] }) =>
      request('/copilot/ask', {
        method: 'POST',
        body: { brand_id: brandId, month, question, history },
      }),
  },

  campaigns: {
    list: (brandId = null) =>
      request(`/campaigns${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ''}`),
    get: (id) => request(`/campaigns/${id}`),
    // The slots a set of options would produce, before anything is generated
    // or paid for.
    preview: (data) => request('/campaigns/preview', { method: 'POST', body: data }),
    plan: (data) => request('/campaigns/plan', { method: 'POST', body: data }),
    update: (id, data) => request(`/campaigns/${id}`, { method: 'PATCH', body: data }),
    // Schedules the whole week. `confirm` is required by the API, not just the
    // UI — a campaign never schedules itself.
    approve: (id) => request(`/campaigns/${id}/approve`, { method: 'POST', body: { confirm: true } }),
    cancel: (id) => request(`/campaigns/${id}/cancel`, { method: 'POST', body: {} }),
    remove: (id) => request(`/campaigns/${id}`, { method: 'DELETE' }),
  },

  /**
   * The unified inbox: comments and DMs in one list, with the state a team
   * keeps on each.
   *
   * Replying is NOT here — it stays on `inbox` below, which owns the 24-hour
   * message window and the claim that stops two runs answering twice. This is
   * the layer around those, never a second way to send.
   */
  /**
   * How a draft compares with what has already worked for this account.
   *
   * `score` is free — it reads analytics already collected and does
   * arithmetic. `critique` and `variations` are the only two that spend.
   *
   * Nothing here predicts reach or clicks. `typical` is what comparable posts
   * actually did, which is history, not a forecast.
   */
  /**
   * Listening monitors.
   *
   * Narrower than the word implies, and the API says so: every read returns a
   * `coverage` note per kind, because Meta has no public-post keyword search.
   * Nothing here spends AI credit.
   */
  listening: {
    get: ({ brandId, days = 30 }) =>
      request(`/listening?brand_id=${encodeURIComponent(brandId)}&days=${days}`),

    readings: (id) => request(`/listening/${id}/readings`),

    create: ({ brandId, name, kind, query }) =>
      request('/listening', { method: 'POST', body: { brand_id: brandId, name, kind, query } }),

    update: (id, body) => request(`/listening/${id}`, { method: 'PATCH', body }),
    remove: (id) => request(`/listening/${id}`, { method: 'DELETE' }),

    run: (id) => request(`/listening/${id}/run`, { method: 'POST', body: {} }),
    runAll: (brandId) => request('/listening/run', { method: 'POST', body: { brand_id: brandId } }),

    seen: ({ id = null, brandId = null }) =>
      request('/listening/alerts/seen', { method: 'POST', body: id ? { id } : { brand_id: brandId } }),
  },

  repurpose: {
    get: (brandId) => request(`/repurpose?brand_id=${encodeURIComponent(brandId)}`),
    job: (id) => request(`/repurpose/${id}`),

    start: ({ brandId, sourceKind, mediaId, title, sourceText, goal }) =>
      request('/repurpose', {
        method: 'POST',
        body: {
          brand_id: brandId,
          source_kind: sourceKind,
          media_id: mediaId,
          title,
          source_text: sourceText,
          goal,
        },
      }),

    editSource: (id, sourceText) =>
      request(`/repurpose/${id}`, { method: 'PATCH', body: { source_text: sourceText } }),

    transcribe: (id) =>
      request(`/repurpose/${id}/transcribe`, { method: 'POST', body: { confirm: true } }),

    generate: (id, channels) =>
      request(`/repurpose/${id}/generate`, { method: 'POST', body: { confirm: true, channels } }),

    draft: (id, channel) =>
      request(`/repurpose/${id}/draft`, { method: 'POST', body: { channel } }),

    remove: (id) => request(`/repurpose/${id}`, { method: 'DELETE' }),
  },

  /**
   * Prompt in, draft out: the video, its captions and the post, chained.
   *
   * `get(id)` is not a passive read — it is what moves a chain along while the
   * screen is open. The five-minute cron does the same when it is not, and
   * neither can do the paid step twice, so polling this is safe.
   */
  pipeline: {
    options: (brandId) => request(`/pipeline?brand_id=${encodeURIComponent(brandId)}`),
    get: (id) => request(`/pipeline/${id}`),

    start: ({ brandId, prompt, goal, channels, duration, resolution, draft }) =>
      request('/pipeline', {
        method: 'POST',
        body: {
          confirm: true,
          brand_id: brandId,
          prompt,
          goal,
          channels,
          duration,
          resolution,
          draft,
        },
      }),

    retry: (id) => request(`/pipeline/${id}/retry`, { method: 'POST', body: {} }),
    cancel: (id) => request(`/pipeline/${id}/cancel`, { method: 'POST', body: {} }),
    remove: (id) => request(`/pipeline/${id}`, { method: 'DELETE' }),
  },

  predict: {
    baseline: ({ brandId, platform }) =>
      request(`/predict/baseline?brand_id=${encodeURIComponent(brandId)}&platform=${platform}`),

    platforms: (brandId) => request(`/predict/platforms?brand_id=${encodeURIComponent(brandId)}`),

    score: ({ brandId, platform, draft, judged = [] }) =>
      request('/predict', {
        method: 'POST',
        body: { brand_id: brandId, platform, ...draft, judged },
      }),

    critique: ({ brandId, platform, draft }) =>
      request('/predict/critique', {
        method: 'POST',
        body: { brand_id: brandId, platform, ...draft },
      }),

    variations: ({ brandId, platform, draft, count = 2 }) =>
      request('/predict/variations', {
        method: 'POST',
        body: { brand_id: brandId, platform, count, ...draft },
      }),
  },

  social: {
    list: ({ brandId = null, tab = 'all' } = {}) => {
      const q = new URLSearchParams({ tab });
      if (brandId) q.set('brand_id', brandId);
      return request(`/social?${q}`);
    },

    stats: ({ brandId = null, days = 30 } = {}) => {
      const q = new URLSearchParams({ days: String(days) });
      if (brandId) q.set('brand_id', brandId);
      return request(`/social/stats?${q}`);
    },

    get: (kind, id) => request(`/social/${kind}/${id}`),

    /** Assign, prioritise, resolve or tag. Local only — the platform sees none of it. */
    setState: (kind, id, body) => request(`/social/${kind}/${id}`, { method: 'PATCH', body }),

    addNote: (kind, id, text) =>
      request(`/social/${kind}/${id}/notes`, { method: 'POST', body: { body: text } }),
    removeNote: (id) => request(`/social/notes/${id}`, { method: 'DELETE' }),

    // The two that spend. Both are asked for; nothing classifies itself.
    triage: ({ brandId, items }) =>
      request('/social/triage', { method: 'POST', body: { brand_id: brandId, items } }),
    suggest: (kind, id) => request(`/social/${kind}/${id}/suggest`, { method: 'POST', body: {} }),

    contact: (id) => request(`/social/contacts/${id}`),
    /** Converting to a lead sets a status and records when. It pushes nowhere. */
    updateContact: (id, body) => request(`/social/contacts/${id}`, { method: 'PATCH', body }),
  },

  inbox: {
    /** @param {'waiting'|'answered'|'all'} [filter] */
    list: ({ brandId = null, filter = 'all' } = {}) => {
      const q = new URLSearchParams({ filter });
      if (brandId) q.set('brand_id', brandId);
      return request(`/inbox?${q}`);
    },
    // Just the number, for the sidebar badge.
    count: () => request('/inbox/count'),
    thread: (id) => request(`/inbox/${id}/thread`),
    // Sent to the platform first; only recorded here once it is accepted.
    reply: (id, message) => request(`/inbox/${id}/reply`, { method: 'POST', body: { message } }),
    /**
     * Replies to a comment with a link to the post itself.
     *
     * `message` is optional — without it the server writes the sentence, which
     * is the whole point of the button.
     */
    sendLink: (id, message) =>
      request(`/inbox/${id}/send-link`, { method: 'POST', body: message ? { message } : {} }),

    /**
     * The same, sent privately rather than as a public reply.
     *
     * The only way to message somebody who has merely commented — Meta's
     * ordinary rule needs them to have written to you first.
     */
    privateReply: (id, message) =>
      request(`/inbox/${id}/private-reply`, {
        method: 'POST',
        body: message ? { message } : {},
      }),

    seen: (id) => request(`/inbox/${id}/seen`, { method: 'POST', body: {} }),
    hide: (id, hidden = true) => request(`/inbox/${id}/hide`, { method: 'POST', body: { hidden } }),
    // Deletes the comment on Facebook or Instagram for everyone, permanently.
    // `confirm` is required by the API as well as asked for on screen.
    remove: (id) => request(`/inbox/${id}/delete`, { method: 'POST', body: { confirm: true } }),
    refresh: () => request('/inbox/refresh', { method: 'POST', body: {} }),

    // ---------- direct messages ----------
    //
    // On the same router as comments, because they are the same job seen from
    // a different angle: somebody said something and is waiting.
    messages: ({ brandId = null, archived = false } = {}) => {
      const q = new URLSearchParams();
      if (brandId) q.set('brand_id', brandId);
      if (archived) q.set('archived', '1');
      return request(`/inbox/messages${q.toString() ? `?${q}` : ''}`);
    },
    conversation: (id) => request(`/inbox/messages/${id}`),
    // Sent to Meta first, and recorded only once Meta has accepted it.
    replyToConversation: (id, message) =>
      request(`/inbox/messages/${id}/reply`, { method: 'POST', body: { message } }),
    conversationSeen: (id) => request(`/inbox/messages/${id}/seen`, { method: 'POST', body: {} }),
    archiveConversation: (id, archived = true) =>
      request(`/inbox/messages/${id}/archive`, { method: 'POST', body: { archived } }),
    refreshMessages: () => request('/inbox/messages/refresh', { method: 'POST', body: {} }),
  },

  ads: {
    // Ad accounts this Facebook login can spend from, plus the spend rails.
    accounts: () => request('/ads/accounts'),
    attach: (brandId, actId) =>
      request('/ads/accounts', { method: 'POST', body: { brand_id: brandId, act_id: actId } }),
    detach: (brandId) => request(`/ads/accounts/${brandId}`, { method: 'DELETE' }),

    goals: () => request('/ads/goals'),
    // Everything the ad builder needs to render: objectives, buttons,
    // countries, limits and the brand's attached app/pixel.
    options: (brandId = null) =>
      request(`/ads/options${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ''}`),
    interests: (q) => request(`/ads/interests?q=${encodeURIComponent(q)}`),
    // Posts already live on the Page and the Instagram account, offered as an
    // advert's creative. Read from Meta, so it includes posts put up outside
    // this app.
    livePosts: (brandId) => request(`/ads/posts?brand_id=${encodeURIComponent(brandId)}`),
    assets: (brandId) => request(`/ads/assets?brand_id=${encodeURIComponent(brandId)}`),
    attachAsset: (data) => request('/ads/assets', { method: 'POST', body: data }),
    detachAsset: (brandId, kind) =>
      request(`/ads/assets/${brandId}/${kind}`, { method: 'DELETE' }),
    // A full ad, with its own creative. Created paused, like a boost.
    createAd: (data) => request('/ads/create', { method: 'POST', body: { ...data, confirm: true } }),
    boosts: (brandId = null) =>
      request(`/ads/boosts${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ''}`),
    boost: (id) => request(`/ads/boosts/${id}`),

    // Creates the boost PAUSED. `confirm` is required by the API, not just the
    // UI — nothing here spends money without being asked twice.
    create: (data) => request('/ads/boost', { method: 'POST', body: { ...data, confirm: true } }),
    // The only call in the app that starts spending.
    start: (id) => request(`/ads/boosts/${id}/start`, { method: 'POST', body: { confirm: true } }),
    // Stopping needs no confirmation: it can only reduce what you spend.
    pause: (id) => request(`/ads/boosts/${id}/pause`, { method: 'POST', body: {} }),

    // What Meta says about this advert right now, level by level — the answer
    // to "where has my ad gone", asked of Meta rather than guessed at.
    delivery: (id) => request(`/ads/boosts/${id}/delivery`),

    // The whole Kreeboom ↔ Meta connection, checked from Meta's side. Read-only.
    connection: () => request('/ads/connection'),

    // Clearing an attempt that never ran. The API refuses anything that is not
    // 'failed', so this cannot throw away a record of real spending.
    dismiss: (id) => request(`/ads/boosts/${id}`, { method: 'DELETE' }),
    dismissAllFailed: () => request('/ads/boosts/dismiss-failed', { method: 'POST', body: {} }),
  },

  generate: {
    models: () => request('/generate/models'),
    captions: (data) => request('/generate/captions', { method: 'POST', body: data }),
  },

  costs: () => request('/costs'),

  /**
   * Videos made with P-Video on Replicate. Starting one spends money and is
   * held to the monthly cap; asking about one also saves it once it is done.
   */
  videos: {
    options: () => request('/videos/options'),
    list: (brandId) => request(`/videos?brand_id=${encodeURIComponent(brandId)}`),
    get: (id) => request(`/videos/${id}`),
    start: (body) => request('/videos', { method: 'POST', body }),
    cancel: (id) => request(`/videos/${id}/cancel`, { method: 'POST', body: {} }),
    /** Streams like a video upload; the length is measured here, where it can be. */
    async uploadAudio(brandId, file, type, seconds) {
      const q = new URLSearchParams({
        brand_id: brandId,
        type,
        name: file.name,
        ...(seconds ? { seconds: String(seconds) } : {}),
      });
      const res = await fetch(`/api/videos/audio?${q}`, { method: 'PUT', body: file });
      return unwrap(res);
    },
  },

  analytics: {
    get: ({ brandId = null, days = 30 } = {}) => {
      const q = new URLSearchParams({ days: String(days) });
      if (brandId) q.set('brand_id', brandId);
      return request(`/analytics?${q}`);
    },
    refresh: () => request('/analytics/refresh', { method: 'POST', body: {} }),
    // Each brand's Instagram account as Meta sees it, side by side. Read-only.
    instagramHealth: () => request('/analytics/instagram-health'),

    // When this brand's posts have actually done best, from its own figures.
    bestTimes: ({ brandId = null, days = 365 } = {}) => {
      const q = new URLSearchParams({ days: String(days) });
      if (brandId) q.set('brand_id', brandId);
      return request(`/analytics/best-times?${q}`);
    },
  },

  /** The bell, and notifications on this device. */
  notifications: {
    list: () => request('/notifications?limit=30'),
    read: (id) => request('/notifications/read', { method: 'POST', body: id ? { id } : {} }),
    pushStatus: () => request('/notifications/push'),
    subscribe: (body) => request('/notifications/push/subscribe', { method: 'POST', body }),
    unsubscribe: (body) => request('/notifications/push/unsubscribe', { method: 'POST', body }),
    pushTest: () => request('/notifications/push/test', { method: 'POST' }),
  },

  settings: {
    /** Proves the alert address works, before it is needed. */
    testAlert: () => request('/settings/alerts/test', { method: 'POST' }),

    /** Every sheet as CSV text, plus what images there are to fetch. */
    /** What a tagged link will look like, before any post carries one. */
    linkPreview: (body) => request('/settings/links/preview', { method: 'POST', body }),

    exportPreview: (brandId) =>
      request(`/settings/export${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ''}`),

    get: () => request('/settings'),
    update: (data) => request('/settings', { method: 'PATCH', body: data }),

    // The MCP interface: whether Claude may connect, and the token it uses.
    mcp: () => request('/settings/mcp'),
    // Returns the token in the clear exactly once; it is stored only hashed.
    newMcpToken: () => request('/settings/mcp/token', { method: 'POST', body: {} }),
    revokeMcpToken: () => request('/settings/mcp/token', { method: 'DELETE' }),
  },

  brands: {
    list: () => request('/brands'),
    get: (id) => request(`/brands/${id}`),
    create: (data) => request('/brands', { method: 'POST', body: data }),
    update: (id, data) => request(`/brands/${id}`, { method: 'PATCH', body: data }),
    archive: (id) => request(`/brands/${id}`, { method: 'DELETE' }),
    options: () => request('/brands/meta/options'),
  },
};
